import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  PackagesSearchPage,
  ProductTypeFunnels,
} from "@b2bportal/core-packages";
import { Funnel, type NavigationFlow } from "@b2bportal/core-types";
import {
  CorePackageSteps,
  getPackageRoutes,
  HotelNavigationUtility,
  useDeviceType,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HotelsShopSelectors } from "@tenant/hotels";
import { PackageHotelAvailabilityPageRoute } from "@tenant/packages";
import { PackageFlightsWrapper } from "../components";
import { PackageCarsAvailabilityPageRoute } from "../features/cars";
import { PackageCarsShopPageRoute } from "../features/cars/pages/PackageCarsShopPage/PackageCarsShopPage.route";
import { PackageHotelShopPageRoute } from "../pages/PackageHotelShopPage/PackageHotelShopPage.route";
import { PackageRoomSelectionPageRoute } from "../pages/PackageHotelShopPage/PackageRoomSelectionPage.route";
import { PackageFlightDeparturePageRoute } from "@/overrides/packages/PackageFlightDeparturePage/PackageFlightDeparturePage.route";
import { RbcPackageFlightReturnPageRoute } from "@/overrides/packages/PackageFlightReturnPage/PackageFlightReturnPage.route";
import { PackageBaseSelectors } from "@/store";

const AdditionalPackageSteps = {
  // Car is not prefixed
  CAR_AVAILABILITY: "AVAILABILITY",
  CAR_SHOP: "SHOP",
  // Book is here to keep smcc unblocked for packages
  BOOK: "BOOK",
  HOTEL_ROOM_SELECTION: "ROOM_SELECTION",
};

export const CombinedPackageSteps = {
  ...CorePackageSteps,
  ...AdditionalPackageSteps,
} as const;
export type CombinedPackageSteps =
  (typeof CombinedPackageSteps)[keyof typeof CombinedPackageSteps];

// Steps included in the flow for each funnel with specific order
const FUNNEL_STEP_ORDER: Partial<Record<Funnel, CombinedPackageSteps[]>> = {
  [Funnel.Lodging]: [
    CombinedPackageSteps.HOTEL_AVAILABILITY,
    CombinedPackageSteps.HOTEL_SHOP,
    CombinedPackageSteps.HOTEL_ROOM_SELECTION,
  ],
  [Funnel.Air]: [
    CombinedPackageSteps.FLIGHT_DEPARTURE,
    CombinedPackageSteps.FLIGHT_RETURN,
  ],
  [Funnel.Ground]: [
    CombinedPackageSteps.CAR_AVAILABILITY,
    CombinedPackageSteps.CAR_SHOP,
  ],
};

export const usePackageFlow = ({
  CheckoutPage,
}: {
  CheckoutPage: React.FC;
}): NavigationFlow<CombinedPackageSteps> => {
  const { BASE_ROUTE, ROUTE_SEGMENTS } = getPackageRoutes();
  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  const searchStep = useMemo(
    () => ({
      [CombinedPackageSteps.SEARCH]: {
        id: CombinedPackageSteps.SEARCH,
        relativePath: `${ROUTE_SEGMENTS.SEARCH}`,
        component: <PackagesSearchPage />,
        show: true,
      },
    }),
    [ROUTE_SEGMENTS.SEARCH]
  );

  const selectedShopHotel = useSelector(HotelsShopSelectors.getSelectedHotel);

  const hotelSteps = useMemo(() => {
    const lodgingName =
      selectedShopHotel?.lodging.name != null
        ? HotelNavigationUtility.placeLabelToUrl(selectedShopHotel.lodging.name)
        : undefined;
    const lodgingId = selectedShopHotel?.lodging.id;

    const shopRelativePath = `${ROUTE_SEGMENTS.HOTELS.SHOP}/:lodgingName?/:lodgingId`;
    const roomSelectionRelativePath = `${ROUTE_SEGMENTS.HOTELS.SHOP}/:lodgingName?/:lodgingId/${ROUTE_SEGMENTS.HOTELS.ROOM_SELECTION}`;

    return {
      [CombinedPackageSteps.HOTEL_AVAILABILITY]: {
        id: CombinedPackageSteps.HOTEL_AVAILABILITY,
        relativePath: ROUTE_SEGMENTS.HOTELS.AVAILABILITY,
        component: <PackageHotelAvailabilityPageRoute />,
        show: true,
        breadcrumb: {
          label: t("core-packages.breadcrumbs.hotelAvailability"),
        },
      },
      [CombinedPackageSteps.HOTEL_SHOP]: {
        id: CombinedPackageSteps.HOTEL_SHOP,
        relativePath: shopRelativePath,
        component: <PackageHotelShopPageRoute />,
        show: true,
        buildPath: () =>
          buildShopPath({
            relativePath: shopRelativePath,
            pathParams: { lodgingName, lodgingId },
          }),
        breadcrumb: {
          label: t("core-packages.breadcrumbs.hotelShop"),
        },
      },
      [CombinedPackageSteps.HOTEL_ROOM_SELECTION]: {
        id: CombinedPackageSteps.HOTEL_ROOM_SELECTION,
        relativePath: roomSelectionRelativePath,
        component: <PackageRoomSelectionPageRoute />,
        show: !isDesktopAndUp,
        buildPath: () =>
          buildShopPath({
            relativePath: roomSelectionRelativePath,
            pathParams: { lodgingName, lodgingId },
          }),
      },
    };
  }, [
    ROUTE_SEGMENTS.HOTELS.AVAILABILITY,
    ROUTE_SEGMENTS.HOTELS.ROOM_SELECTION,
    ROUTE_SEGMENTS.HOTELS.SHOP,
    isDesktopAndUp,
    selectedShopHotel?.lodging.id,
    selectedShopHotel?.lodging.name,
    t,
  ]);

  const flightSteps = useMemo(
    () => ({
      [CombinedPackageSteps.FLIGHT_DEPARTURE]: {
        id: CombinedPackageSteps.FLIGHT_DEPARTURE,
        relativePath: ROUTE_SEGMENTS.FLIGHTS.DEPARTURE,
        component: (
          <PackageFlightsWrapper>
            <PackageFlightDeparturePageRoute />
          </PackageFlightsWrapper>
        ),
        show: true,
        breadcrumb: {
          label: t("core-packages.breadcrumbs.flightDeparture"),
        },
      },
      [CombinedPackageSteps.FLIGHT_RETURN]: {
        id: CombinedPackageSteps.FLIGHT_RETURN,
        relativePath: ROUTE_SEGMENTS.FLIGHTS.RETURN,
        component: (
          <PackageFlightsWrapper>
            <RbcPackageFlightReturnPageRoute />
          </PackageFlightsWrapper>
        ),
        show: true,
        breadcrumb: {
          label: t("core-packages.breadcrumbs.flightReturn"),
        },
      },
    }),
    [ROUTE_SEGMENTS.FLIGHTS.DEPARTURE, ROUTE_SEGMENTS.FLIGHTS.RETURN, t]
  );

  const carSteps = useMemo(
    () => ({
      [CombinedPackageSteps.CAR_AVAILABILITY]: {
        id: CombinedPackageSteps.CAR_AVAILABILITY,
        relativePath: ROUTE_SEGMENTS.CARS.AVAILABILITY,
        component: <PackageCarsAvailabilityPageRoute />,
        show: true,
        breadcrumb: {
          label: t("core-packages.breadcrumbs.carAvailability"),
        },
      },
      // We don't show the shop step in the flow, we go straight to checkout.
      [CombinedPackageSteps.CAR_SHOP]: {
        id: CombinedPackageSteps.CAR_SHOP,
        relativePath: ROUTE_SEGMENTS.CARS.SHOP,
        component: <PackageCarsShopPageRoute />,
        show: false,
        // breadcrumb: {
        //   label: t("core-packages.breadcrumbs.carShop"),
        // },
      },
    }),
    [ROUTE_SEGMENTS.CARS.AVAILABILITY, ROUTE_SEGMENTS.CARS.SHOP, t]
  );

  // NOTE: RBC does not have a review page, we go straight to checkout.
  const checkoutSteps = useMemo(
    () => ({
      [CombinedPackageSteps.BOOK]: {
        id: CombinedPackageSteps.BOOK,
        relativePath: ROUTE_SEGMENTS.CHECKOUT,
        component: <CheckoutPage />,
        show: true,
        breadcrumb: {
          label: t("core-packages.breadcrumbs.checkout"),
        },
      },
    }),
    [ROUTE_SEGMENTS.CHECKOUT, CheckoutPage, t]
  );

  const funnelsNeeded = useMemo(
    () => ProductTypeFunnels[packageType],
    [packageType]
  );

  return useMemo(() => {
    const stepOrder: CombinedPackageSteps[] = [
      CombinedPackageSteps.SEARCH,
      ...funnelsNeeded.flatMap((funnel) => FUNNEL_STEP_ORDER[funnel] ?? []),
      CombinedPackageSteps.BOOK,
    ];

    return {
      stepOrder,
      steps: {
        ...searchStep,
        ...hotelSteps,
        ...flightSteps,
        ...carSteps,
        ...checkoutSteps,
      },
      funnelPath: BASE_ROUTE,
    };
  }, [
    funnelsNeeded,
    searchStep,
    hotelSteps,
    flightSteps,
    carSteps,
    checkoutSteps,
    BASE_ROUTE,
  ]);
};

interface ShopPathParams {
  lodgingName?: string;
  lodgingId?: string;
}

interface ShopPathOptions {
  relativePath: string;
  pathParams: ShopPathParams;
}

const buildShopPath = ({ relativePath, pathParams }: ShopPathOptions): string =>
  relativePath
    .replace(
      ":lodgingName?/",
      pathParams.lodgingName != null ? `${pathParams.lodgingName}/` : ""
    )
    .replace(":lodgingId", pathParams.lodgingId ?? "");
